import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getRegistrationTrends, PatientManagementTrendsPayload } from '../../services';
import { translateLabelByTimeRange } from '../../utils';
import { ROLES } from '../../constants';

interface RegistrationTrendItem {
  label: string;
  total: number;
}

interface RegistrationTrendsChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
  }[];
}

export const useRegistrationTrends = (
  payload: PatientManagementTrendsPayload,
  selectedRange: string
) => {
  const { t } = useTranslation();
  const userRole = localStorage.getItem('userRole') || '';

  const query = useQuery<RegistrationTrendItem[]>({
    queryKey: ['registration-trends', payload, selectedRange, userRole],
    queryFn: async () => {
      const result = await getRegistrationTrends(payload);

      const trends: RegistrationTrendItem[] =
        userRole === ROLES.PRACTISE_ADMIN &&
        result?.get_practise_admin_patient_management_registration_trend
          ? result.get_practise_admin_patient_management_registration_trend
          : result?.get_patient_management_registration_trend || [];

      return trends;
    },
    enabled: !!payload,
    staleTime: 5 * 60 * 1000,
    retry: 1
  });

  const translatedTrends = useMemo(() => {
    return (
      query.data?.map((item) => ({
        ...item,
        label: translateLabelByTimeRange(item.label, selectedRange, t)
      })) ?? []
    );
  }, [query.data, selectedRange, t]);

  const chartData: RegistrationTrendsChartData = useMemo(() => {
    if (!translatedTrends.length) return { labels: [], datasets: [] };

    const labels = translatedTrends.map((item) => item.label);

    const datasets = [
      {
        label: t('patients'),
        data: translatedTrends.map((item) => item.total)
      }
    ];

    return { labels, datasets };
  }, [translatedTrends, t]);

  return {
    ...query,
    translatedTrends,
    chartData
  };
};
